import { forwardRef, useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useI18n } from '@/lib/i18n'

export interface SaverProps {
  videoSrc: string
  phrases: string[]
  intervalSeconds: number
  maxMessages: number
  wakeMethod: 'touch' | 'motion'
}

export type DrawFn = (ctx: CanvasRenderingContext2D, w: number, h: number, t: number, dt: number) => void

// Kiosk screens are often 4K panels driven by modest GPUs. Particle effects
// look the same at 1.5x as at 2x, so cap the backing store there.
const MAX_DPR = 1.5

/** Runs `draw` every frame on a canvas that tracks its CSS size. */
export function useCanvasLoop(canvasRef: RefObject<HTMLCanvasElement>, draw: DrawFn) {
  const drawRef = useRef(draw)
  drawRef.current = draw
  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    let w = 0
    let h = 0
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR)
      w = canvas.clientWidth
      h = canvas.clientHeight
      canvas.width = Math.max(1, Math.round(w * dpr))
      canvas.height = Math.max(1, Math.round(h * dpr))
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    const ro = new ResizeObserver(resize)
    ro.observe(canvas)
    const start = performance.now()
    let last = start
    let raf = 0
    const frame = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000)
      last = now
      ctx.clearRect(0, 0, w, h)
      if (w > 0 && h > 0) drawRef.current(ctx, w, h, (now - start) / 1000, dt)
      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [canvasRef])
}

/** Per-frame callback for effects that write straight to DOM refs (no React renders). */
export function useFrame(cb: (t: number, dt: number) => void) {
  const cbRef = useRef(cb)
  cbRef.current = cb
  useEffect(() => {
    const start = performance.now()
    let last = start
    let raf = 0
    const frame = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000)
      last = now
      cbRef.current((now - start) / 1000, dt)
      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(raf)
  }, [])
}

const shuffled = (n: number) => Array.from({ length: n }, (_, i) => i).sort(() => Math.random() - 0.5)

/** Cycles through the operator's hype phrases, shuffled, one at a time. */
export function usePhraseCycle(phrases: string[], seconds: number) {
  const list = phrases.map((p) => p.trim()).filter(Boolean)
  const key = list.join('\n')
  const [state, setState] = useState(() => ({ order: shuffled(list.length), i: 0 }))
  const firstRun = useRef(true)
  useEffect(() => {
    // Reshuffle only when the list itself changes, so mounting shows one phrase without a swap
    if (!firstRun.current) setState({ order: shuffled(list.length), i: 0 })
    firstRun.current = false
    if (list.length < 2) return
    const id = setInterval(() => {
      setState((s) => (s.i + 1 >= s.order.length ? { order: shuffled(list.length), i: 0 } : { ...s, i: s.i + 1 }))
    }, Math.max(2, seconds) * 1000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, seconds])
  if (!list.length) return null
  const index = state.order[state.i] ?? 0
  return { text: list[index] ?? list[0], id: `${state.i}-${index}` }
}

export function useCtaText(wakeMethod: 'touch' | 'motion') {
  const { t } = useI18n()
  return wakeMethod === 'motion' ? t('screensaver.stepCloser') : t('screensaver.tapToStart')
}

export const BgVideo = forwardRef<HTMLVideoElement, { src: string; style?: CSSProperties; className?: string }>(
  function BgVideo({ src, style, className = '' }, ref) {
    return (
      <video
        ref={ref}
        src={src}
        autoPlay
        loop
        muted
        playsInline
        className={`absolute inset-0 h-full w-full object-cover ${className}`}
        style={style}
      />
    )
  },
)

/** Crossfading single line of text, keyed on the phrase. */
export function PhraseSwap({ phrase, className, style }: { phrase: { text: string; id: string } | null; className?: string; style?: CSSProperties }) {
  return (
    <div className="relative w-full" style={{ minHeight: '1.4em', ...style }}>
      <AnimatePresence mode="wait">
        {phrase && (
          <motion.div
            key={phrase.id}
            className={className}
            initial={{ opacity: 0, y: 14, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: -10, filter: 'blur(6px)' }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          >
            {phrase.text}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export const clamp01 = (n: number) => Math.max(0, Math.min(1, n))
export const lerp = (a: number, b: number, k: number) => a + (b - a) * k
export const easeOutCubic = (k: number) => 1 - Math.pow(1 - k, 3)
export const easeInOutCubic = (k: number) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2)
export const rand = (a: number, b: number) => a + Math.random() * (b - a)
export const pick = <T,>(items: readonly T[]) => items[Math.floor(Math.random() * items.length)]
